import { useEffect, useMemo, useRef, useState } from 'react'
import { useClearQueue, useQueues } from '@/api/queries'
import type { QueueInfo } from '@/api/types'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useToast } from '@/components/ui/Toast'
import { MonitoringCard } from './MonitoringCard'
import { QueueList } from './QueueList'
import { formatCount, queueKey, targetLabel, type Target } from './target'

interface QueuesCardProps {
  target: Target
  open: boolean
  onToggle: () => void
}

export function QueuesCard({ target, open, onToggle }: QueuesCardProps) {
  const { data, error, isLoading, isFetching, refetch, dataUpdatedAt } = useQueues(target, open)
  const clear = useClearQueue()
  const toast = useToast()
  const [pending, setPending] = useState<QueueInfo | null>(null)

  // Counts of the previous fetch. The memo reads them before the effect below overwrites them,
  // and keys on dataUpdatedAt because an unchanged refetch keeps the same `data` reference.
  const previousCounts = useRef(new Map<string, number>())
  const deltas = useMemo(() => {
    const changes = new Map<string, number>()
    for (const queue of data ?? []) {
      const before = previousCounts.current.get(queueKey(queue))
      if (before !== undefined && before !== queue.messageCount) changes.set(queueKey(queue), queue.messageCount - before)
    }
    return changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataUpdatedAt])
  useEffect(() => {
    previousCounts.current = new Map((data ?? []).map((queue) => [queueKey(queue), queue.messageCount]))
  }, [data, dataUpdatedAt])

  const confirmClear = async (queue: QueueInfo) => {
    try {
      await clear.mutateAsync({ ...target, storage: queue.storage, queue: queue.name })
      toast.notify(`Messages cleared in ${queue.name}`, 'success')
    } catch (err) {
      toast.error(err, 'Clear failed')
    } finally {
      setPending(null)
    }
  }

  return (
    <>
      <MonitoringCard title={targetLabel(target)} open={open} onToggle={onToggle} onRefresh={() => void refetch()} fetching={isFetching}>
        {(query) => <QueueList queues={data} loading={isLoading} error={error} query={query} deltas={deltas} onClear={setPending} />}
      </MonitoringCard>
      {pending && (
        <ConfirmDialog
          title={`Clear ${pending.name}`}
          message={`All ${formatCount(pending.messageCount)} messages in ${pending.storage}/${pending.name} (${targetLabel(target)}) are deleted. This cannot be undone.`}
          confirmLabel="Clear messages"
          danger
          busy={clear.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => void confirmClear(pending)}
        />
      )}
    </>
  )
}
